/* eslint-disable jsdoc/no-undefined-types */
/* eslint-disable jsdoc/check-types */
/* eslint-disable jsdoc/check-param-names */
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import {
  Person,
  Edit,
  LocationOn,
  Phone,
  Email,
  Business,
  AccountBalance,
} from "@material-ui/icons";
import sqlToStandardDate from "../../../../../components/shared/functions/sqlToStandardDate";
import { bankAccountTypes } from "../../../../../components/shared/constants";

/**
 * A comprehensive React component that displays all dealer information in a single card.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.dealer - The dealer object containing all information.
 * @param {Object} props.bankingDetails - The banking details object with bank info.
 * @param {string} props.userProductTypesState - Selected product types.
 * @param {string} props.userSuppliersState - Selected suppliers.
 * @param {Function} props.onEditClick - Function to handle edit button click.
 * @param {Object} props.currentUser - The current user object containing all information.
 * @returns {JSX.Element} A comprehensive card component with all dealer information.
 */
export default function UserInfoCard({
  dealer,
  bankingDetails,
  userProductTypesState,
  userSuppliersState,
  onEditClick,
  currentUser,
}) {
  return (
    <Card elevation={2} sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header with Edit Button */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Dealer Information
          </Typography>
          {currentUser?.is_admin ? (
            <Button
              startIcon={<Edit />}
              variant="contained"
              size="small"
              onClick={onEditClick}
              sx={{ borderRadius: 2 }}
            >
              Edit
            </Button>
          ) : null}
        </Stack>

        {/* Personal Information Section */}
        <Box mb={4}>
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
              mb: 2,
            }}
          >
            <Person color="primary" />
            Personal Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {dealer.first_name} {dealer.middle_name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Surname
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {dealer.surname}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  User Code
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Z-{dealer.id}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Date Created
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {sqlToStandardDate(dealer.created_at)}
                </Typography>
              </Box>
            </Grid>
            {currentUser?.is_admin && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    ID Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dealer.identification_no}
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  T-Shirt Size
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {dealer.refs_shirt_size || "Not Given"}
                </Typography>
              </Box>
            </Grid>
          </Grid>{" "}
          <Divider sx={{ my: 3 }} />
        </Box>

        {/* Contact Information Section */}
        <Box mb={4}>
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
              mb: 2,
            }}
          >
            <Phone color="primary" />
            Contact Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Phone fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dealer.phone}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Email fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dealer.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>{" "}
          <Divider sx={{ my: 3 }} />
        </Box>

        {/* Address Information Section */}
        <Box mb={4}>
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
              mb: 2,
            }}
          >
            <LocationOn color="primary" />
            Address Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Address Line 1
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {dealer.street}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Suburb
              </Typography>
              <Typography variant="body1">{dealer.suburb}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                City
              </Typography>
              <Typography variant="body1">{dealer.city}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Province
              </Typography>
              <Typography variant="body1">{dealer.province}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Postal Code
              </Typography>
              <Typography variant="body1">{dealer.postal_code}</Typography>
            </Grid>
          </Grid>{" "}
          <Divider sx={{ my: 3 }} />
        </Box>

        {/* Payment Information Section */}
        {currentUser?.is_admin && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 600,
                mb: 2,
              }}
            >
              <AccountBalance color="primary" />
              Payment Information
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Payment Type
                </Typography>
                <Chip
                  label={dealer.refs_payment_type}
                  color="primary"
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                />{" "}
              </Box>

              {dealer.refs_payment_type === "eWallet" ? (
                <>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      eWallet Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dealer.alt_phone ? dealer.alt_phone : dealer.phone}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      eWallet Link
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dealer.e_wallet_link === "undefined" ||
                      dealer.e_wallet_link === "null" ||
                      !dealer.e_wallet_link
                        ? "Not populated"
                        : dealer.e_wallet_link}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      Bank
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {bankingDetails.bank}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      Account Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {bankingDetails.account_type &&
                        bankAccountTypes.filter(
                          item => item.value === bankingDetails.account_type
                        )[0].label}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      Account Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {bankingDetails.account_number}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Stack>{" "}
            <Divider sx={{ my: 3 }} />
          </Box>
        )}

        <Box mb={4}>
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
              mb: 2,
            }}
          >
            <Business color="primary" />
            Suppliers and Product Types
            <Typography variant="caption" color="textSecondary">
              (Tip: edit in quick actions)
            </Typography>
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Selected Product Types
              </Typography>
              <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                {userProductTypesState}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Selected Suppliers
              </Typography>
              <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                {userSuppliersState}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
